import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Folder,
  HardDrive,
  Loader2,
  LogIn,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { googleDriveApi, type GoogleDriveStatus } from '../lib/api';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';

function errorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string; message?: string } } }).response;
    return response?.data?.error || response?.data?.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

function formatDate(value: string | null): string {
  if (!value) return 'Brak danych';
  return new Date(value).toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function GoogleDrivePage() {
  const location = useLocation();
  const confirm = useConfirmDialog();
  const [status, setStatus] = useState<GoogleDriveStatus | null>(null);
  const [folder, setFolder] = useState('');
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [savingFolder, setSavingFolder] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queryState = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const loadStatus = async () => {
    try {
      setError(null);
      const res = await googleDriveApi.getStatus();
      setStatus(res.data.data);
      if (res.data.data.rootFolderId && !folder) {
        setFolder(res.data.data.rootFolderId);
      }
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się pobrać statusu Google Drive.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const oauthResult = queryState.get('googleDrive');
    if (oauthResult === 'connected') {
      setMessage('Konto Google zostało połączone.');
    }
    if (oauthResult === 'error') {
      setError(queryState.get('message') || 'Nie udało się połączyć Google Drive.');
    }
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const res = await googleDriveApi.startOAuth({ returnTo: '/integrations/google-drive' });
      window.location.href = res.data.data.url;
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się rozpocząć logowania Google.'));
      setConnecting(false);
    }
  };

  const handleSaveFolder = async () => {
    setSavingFolder(true);
    setError(null);
    setMessage(null);
    try {
      const res = await googleDriveApi.setRootFolder(folder);
      setStatus(res.data.data);
      setMessage('Folder główny został zapisany.');
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się zapisać folderu głównego.'));
    } finally {
      setSavingFolder(false);
    }
  };

  const handleCreateFolder = async () => {
    setCreatingFolder(true);
    setError(null);
    setMessage(null);
    try {
      const res = await googleDriveApi.createRootFolder('EcomFlow');
      setStatus(res.data.data);
      setFolder(res.data.data.rootFolderId || '');
      setMessage('Folder EcomFlow został utworzony i ustawiony jako główny.');
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się utworzyć folderu na Dysku Google.'));
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDisconnect = async () => {
    const confirmed = await confirm({
      title: 'Odłączyć Dysk Google?',
      message: 'EcomFlow utraci dostęp do zapisu nowych kreacji na tym koncie Google.',
      confirmLabel: 'Odłącz',
      cancelLabel: 'Anuluj',
      danger: true,
    });
    if (!confirmed) return;

    setDisconnecting(true);
    setError(null);
    setMessage(null);
    try {
      await googleDriveApi.disconnect();
      setFolder('');
      setMessage('Dysk Google został odłączony.');
      await loadStatus();
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się odłączyć Google Drive.'));
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Dysk Google</h1>
          <p className="text-text-secondary mt-2">
            Zapisuj grafiki i wideo z Laboratorium na zewnętrznym dysku - gotowe do pracy z zewnętrznymi narzędziami.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={loadStatus}
          leftIcon={<RefreshCw />}
        >
          Odśwież
        </Button>
      </div>

      {message && <Alert variant="success" title="Gotowe" onClose={() => setMessage(null)}>{message}</Alert>}
      {error && <Alert variant="danger" title="Błąd" onClose={() => setError(null)}>{error}</Alert>}

      {status && !status.configured && (
        <Alert variant="warning" title="Brak konfiguracji backendu">
          To konfiguracja jednorazowa dla aplikacji EcomFlow. Administrator wdrożenia musi ustawić GOOGLE_CLIENT_ID i GOOGLE_CLIENT_SECRET, a użytkownicy będą potem tylko logować się na swoje konto Google i wybierać folder. Callback OAuth: {status.redirectUri}
        </Alert>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6">
        <div className="card space-y-6">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${status?.connected ? 'bg-success/10' : 'bg-surface-hover'}`}>
              <HardDrive className={`w-6 h-6 ${status?.connected ? 'text-success' : 'text-text-muted'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-text-primary">Połączenie Google</h2>
                {status?.connected ? (
                  <span className="badge badge-success">Połączone</span>
                ) : (
                  <span className="badge badge-error">Niepołączone</span>
                )}
              </div>
              <p className="text-sm text-text-secondary mt-1">
                Ostatnie połączenie: {formatDate(status?.connectedAt || null)}
              </p>
            </div>
          </div>

          {!status?.connected ? (
            <Button
              onClick={handleConnect}
              loading={connecting}
              disabled={!status?.configured}
              leftIcon={<LogIn />}
            >
              Połącz konto Google
            </Button>
          ) : (
            <div className="space-y-5">
              <div className="rounded-xl border border-border bg-surface-hover/30 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-text-primary mb-3">
                  <Folder className="w-4 h-4 text-accent-primary" />
                  Folder główny
                </div>
                {status.rootFolderId ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-text-primary truncate">
                          {status.rootFolderName || status.rootFolderId}
                        </p>
                        <p className="text-xs text-text-muted truncate">{status.rootFolderId}</p>
                      </div>
                      {status.rootFolderUrl && (
                        <a
                          href={status.rootFolderUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-accent-primary hover:text-accent-info"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Otwórz
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-text-muted">Brak ustawionego folderu głównego.</p>
                )}
              </div>

              <div className="space-y-3">
                <label className="space-y-1.5 block">
                  <span className="text-xs font-medium text-text-secondary">ID albo link folderu</span>
                  <input
                    value={folder}
                    onChange={e => setFolder(e.target.value)}
                    placeholder="https://drive.google.com/drive/folders/..."
                    className="input w-full"
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={handleSaveFolder}
                    loading={savingFolder}
                    disabled={!folder.trim()}
                    leftIcon={<CheckCircle />}
                  >
                    Zapisz folder
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCreateFolder}
                    loading={creatingFolder}
                    leftIcon={<Folder />}
                  >
                    Utwórz folder EcomFlow
                  </Button>
                </div>
              </div>

              <div className="pt-4 border-t border-border-subtle">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDisconnect}
                  loading={disconnecting}
                  leftIcon={<Trash2 />}
                >
                  Odłącz integrację
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="card space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-5 h-5 ${status?.connected && status.rootFolderId ? 'text-success' : 'text-text-muted'}`} />
            <h2 className="text-lg font-semibold text-text-primary">Status integracji</h2>
          </div>

          <div className="rounded-xl border border-border bg-surface-hover/20 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-text-secondary">Połączenie</span>
              <span className={status?.connected ? 'text-success' : 'text-danger'}>
                {status?.connected ? 'Połączone' : 'Niepołączone'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-text-secondary">Folder roboczy</span>
              <span className={status?.rootFolderId ? 'text-success' : 'text-text-muted'}>
                {status?.rootFolderId ? 'Ustawiony' : 'Brak'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-text-secondary">Ostatnie połączenie</span>
              <span className="text-text-primary text-right">{formatDate(status?.connectedAt || null)}</span>
            </div>
          </div>

          <div className="rounded-xl border border-accent-primary/20 bg-accent-primary/5 p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-accent-primary" />
              <h3 className="text-sm font-semibold text-text-primary">Jak to działa?</h3>
            </div>
            <p className="text-xs text-text-muted mt-2 leading-relaxed">
              EcomFlow zapisuje grafiki, wideo i inne pliki multimedialne z Laboratorium w wybranym folderze Dysku Google. W tym folderze tworzy też podfoldery robocze potrzebne do kolejnych pipeline'ów.
            </p>
          </div>

          <div className="rounded-xl border border-success/20 bg-success/5 p-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-success" />
              <h3 className="text-sm font-semibold text-text-primary">Bezpieczeństwo</h3>
            </div>
            <p className="text-xs text-text-muted mt-2 leading-relaxed">
              Integracja jest ograniczona do zapisu i obsługi plików w folderze roboczym wskazanym w EcomFlow oraz w podfolderach utworzonych przez system. Pozostałe pliki na Dysku Google nie są przeglądane ani synchronizowane przez EcomFlow.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
